import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save, Calculator, TrendingUp, CheckCircle2, Star, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const AdminMortgage = () => {
  useSeo({ title: "Hypotheek · Admin", description: "Hypotheek instellingen" });
  const [cfg, setCfg] = useState(null);
  const [partners, setPartners] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [days, setDays] = useState(30);
  const [savingCfg, setSavingCfg] = useState(false);
  const [savingPartnerId, setSavingPartnerId] = useState(null);
  const [err, setErr] = useState("");

  const loadAll = () => {
    setErr("");
    api("/admin/mortgage-config").then(setCfg).catch((e) => setErr(e.message));
    api("/admin/partners?lead_type=mortgage").then((d) => setPartners(d.rows || [])).catch(() => {});
    api(`/admin/mortgage-funnel?days=${days}`).then(setFunnel).catch(() => {});
  };
  useEffect(loadAll, [days]);

  const primary = useMemo(
    () => (partners || []).find((p) => p.is_primary_mortgage_partner && p.active),
    [partners],
  );

  const saveCfg = async () => {
    if (!cfg) return;
    setSavingCfg(true);
    try {
      const r = await api("/admin/mortgage-config", {
        method: "PUT",
        body: JSON.stringify({ rate_pct: Number(cfg.rate_pct), term_years: Number(cfg.term_years) }),
      });
      setCfg(r);
      toast.success("Rente & looptijd opgeslagen");
    } catch (e) {
      toast.error(e.message || "Opslaan mislukt");
    } finally { setSavingCfg(false); }
  };

  const togglePrimary = async (p) => {
    setSavingPartnerId(p.id);
    try {
      await api(`/admin/partners/${p.id}`, {
        method: "PUT",
        body: JSON.stringify({ ...p, is_primary_mortgage_partner: !p.is_primary_mortgage_partner }),
      });
      toast.success(!p.is_primary_mortgage_partner ? `${p.name} is nu de primaire partner` : "Primaire partner verwijderd");
      api("/admin/partners?lead_type=mortgage").then((d) => setPartners(d.rows || []));
    } catch (e) {
      toast.error(e.message || "Opslaan mislukt");
    } finally { setSavingPartnerId(null); }
  };

  const maxStage = Math.max(1, ...(funnel?.stages || []).map((s) => s.count));

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Link to="/admin" className="inline-flex items-center gap-1 hover:text-navy"><ArrowLeft size={14} /> Admin</Link>
      </div>
      <div className="mt-2 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-navy" data-testid="admin-mortgage-title">Hypotheek instellingen</h1>
          <p className="text-sm text-slate-500">Beheer rente, looptijd en welke partner nieuwe hypotheek-leads ontvangt.</p>
        </div>
        <Button variant="ghost" size="sm" onClick={loadAll}><RefreshCw size={14} className="mr-1" /> Ververs</Button>
      </div>

      {err && <div className="mt-4 rounded-xl bg-danger/10 p-4 text-sm text-danger">{err}</div>}

      {/* 1. Rate & term */}
      <section className="mt-6 rounded-3xl border border-border bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2">
          <Calculator size={18} className="text-brandblue" />
          <h2 className="font-display text-lg font-extrabold text-navy">Rente & looptijd voor de berekening</h2>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Deze waarden worden gebruikt in de widget &quot;Wat worden mijn maandlasten?&quot; op elke koopwoning. Standaard: 10 jaar vast NHG, annuïtair.
        </p>

        {!cfg ? (
          <div className="mt-4 flex items-center gap-2 text-slate-500"><Loader2 size={14} className="animate-spin" /> Laden…</div>
        ) : (
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <div>
              <Label className="text-xs font-semibold text-slate-600">Rente (% per jaar)</Label>
              <Input
                type="number" step="0.01" min="0.1" max="20"
                value={cfg.rate_pct}
                onChange={(e) => setCfg({ ...cfg, rate_pct: e.target.value })}
                data-testid="mcfg-rate"
                className="mt-1 rounded-xl"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Looptijd (jaar)</Label>
              <Input
                type="number" min="5" max="40"
                value={cfg.term_years}
                onChange={(e) => setCfg({ ...cfg, term_years: e.target.value })}
                data-testid="mcfg-term"
                className="mt-1 rounded-xl"
              />
            </div>
            <div className="flex items-end">
              <Button onClick={saveCfg} disabled={savingCfg} className="w-full rounded-xl bg-navy text-amber-300 hover:bg-navy/90" data-testid="mcfg-save">
                {savingCfg ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />} Opslaan
              </Button>
            </div>
            {cfg.updated_at && (
              <div className="sm:col-span-3 text-[11px] text-slate-400">
                Laatst bijgewerkt: {new Date(cfg.updated_at).toLocaleString("nl-NL")}
              </div>
            )}
          </div>
        )}
      </section>

      {/* 2. Primary partner */}
      <section className="mt-6 rounded-3xl border border-border bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2">
          <Star size={18} className="text-amber-500" />
          <h2 className="font-display text-lg font-extrabold text-navy">Primaire hypotheekpartner</h2>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Deze partner ontvangt automatisch alle nieuwe hypotheek-leads uit de widget. Slechts één tegelijk; schakelen past automatisch de andere aan.
        </p>

        {primary ? (
          <div className="mt-4 flex items-center justify-between rounded-2xl bg-success/10 p-4" data-testid="primary-partner-banner">
            <div>
              <div className="flex items-center gap-2 text-success">
                <CheckCircle2 size={16} />
                <span className="text-xs font-semibold uppercase tracking-wide">Actief</span>
              </div>
              <div className="mt-1 font-display text-lg font-extrabold text-navy">{primary.name}</div>
              <div className="text-xs text-slate-500">{primary.contact_name}{primary.email ? ` · ${primary.email}` : ""}</div>
            </div>
            <Button asChild variant="outline" className="rounded-xl">
              <Link to={`/admin/partners/${primary.id}`}>Bewerken</Link>
            </Button>
          </div>
        ) : (
          <div className="mt-4 rounded-2xl bg-amber-50 p-4 text-sm text-amber-900" data-testid="no-primary-banner">
            Geen primaire hypotheekpartner ingesteld — nieuwe leads worden niet automatisch doorgezet. Kies hieronder een partner, of maak eerst een partner aan.
          </div>
        )}

        <div className="mt-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Partners die hypotheekleads accepteren</h3>
          {!partners ? (
            <div className="mt-2 flex items-center gap-2 text-slate-500"><Loader2 size={14} className="animate-spin" /> Laden…</div>
          ) : partners.length === 0 ? (
            <div className="mt-3 rounded-2xl border border-dashed border-border p-6 text-center text-sm text-slate-500">
              Nog geen partners met leadtype &quot;Hypotheek&quot;.{" "}
              <Link to="/admin/partners/new" className="text-brandblue underline">Nieuwe partner aanmaken</Link> en vink &quot;Hypotheek&quot; aan.
            </div>
          ) : (
            <ul className="mt-3 divide-y divide-border overflow-hidden rounded-2xl border border-border">
              {partners.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-3 p-3" data-testid={`partner-row-${p.id}`}>
                  <div className="min-w-0">
                    <Link to={`/admin/partners/${p.id}`} className="font-semibold text-navy hover:text-brandblue">{p.name}</Link>
                    <div className="text-xs text-slate-500">
                      {p.active ? <span className="text-success">Actief</span> : <span className="text-slate-400">Inactief</span>}
                      {p.price_per_lead != null && <> · €{p.price_per_lead}/lead</>}
                      {(p.regions || []).length > 0 && <> · {p.regions.slice(0, 3).join(", ")}</>}
                    </div>
                  </div>
                  <Button
                    variant={p.is_primary_mortgage_partner ? "default" : "outline"}
                    className={`rounded-xl ${p.is_primary_mortgage_partner ? "bg-amber-500 text-white hover:bg-amber-600" : ""}`}
                    onClick={() => togglePrimary(p)}
                    disabled={savingPartnerId === p.id || !p.active}
                    data-testid={`primary-toggle-${p.id}`}
                  >
                    {savingPartnerId === p.id
                      ? <Loader2 size={14} className="animate-spin" />
                      : (<><Star size={14} className="mr-1.5" /> {p.is_primary_mortgage_partner ? "Primair" : "Maak primair"}</>)}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* 3. Funnel */}
      <section className="mt-6 rounded-3xl border border-border bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <TrendingUp size={18} className="text-brandblue" />
            <h2 className="font-display text-lg font-extrabold text-navy">Funnel</h2>
          </div>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="rounded-xl border border-border bg-white px-3 py-1.5 text-sm"
            data-testid="funnel-days"
          >
            <option value={7}>Afgelopen 7 dagen</option>
            <option value={30}>Afgelopen 30 dagen</option>
            <option value={90}>Afgelopen 90 dagen</option>
            <option value={365}>Afgelopen jaar</option>
          </select>
        </div>

        {!funnel ? (
          <div className="mt-4 flex items-center gap-2 text-slate-500"><Loader2 size={14} className="animate-spin" /> Laden…</div>
        ) : (
          <ul className="mt-4 space-y-2" data-testid="funnel-list">
            {funnel.stages.map((s) => {
              const pct = Math.round((s.count / maxStage) * 100);
              return (
                <li key={s.name} className="rounded-xl bg-slate-50 p-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-navy">{s.name}</span>
                    <span className="tabular-nums font-bold text-navy">{s.count}</span>
                  </div>
                  <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-slate-200">
                    <div className="h-full rounded-full bg-brandblue transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <p className="mt-3 text-[11px] text-slate-400">
          CTA views/clicks/berekeningen komen uit analytics_events; leads/afspraken/klanten uit de CRM.
        </p>
      </section>
    </div>
  );
};

export default AdminMortgage;
